import { DataService } from 'src/app/services/data.service';
import { BreadCrumb } from './../../../../../interfaces/main';
import { AuthService } from './../../../../../services/auth.service';
import { Student } from './../../../../../interfaces/user';
import { Session } from 'src/app/interfaces/course';
import { Lesson, Material, Drawing } from './../../../../../interfaces/course';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { Observable, BehaviorSubject } from 'rxjs';
import { DatePipe } from '@angular/common';
import swal from 'sweetalert2';
import * as uuid from 'uuid/v1';

@Component({
  selector: 'app-single-session',
  templateUrl: './session.component.html',
  styleUrls: ['./session.component.scss']
})

export class SingleCourseSessionComponent implements OnInit {
  isSchoolAdmin$: Observable<any>;
  isTeacher = false;

  isLoadingMaterials = false;
  isLoadingDrawings = false;

  lesson: Lesson;

  currentSession: Session;

  user: Student;

  materials: Material[];
  drawings: Drawing[];

  activeDrawing: Drawing;

  activeDrawing$ = new BehaviorSubject(null);

  breadcrumbs: BreadCrumb[] = [{
    link: 'Home',
    url: ['/']
  },{
    link: 'Courses',
    url: ['/courses']
  }];

  constructor(
    private route: ActivatedRoute,
    private data: DataService,
    private router: Router,
    private toastr: ToastrService,
    private auth: AuthService,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {

    this.isSchoolAdmin$ = this.auth.isSchoolAdmin();

    this.route.params.subscribe(async (p) => {
      if(p.courseid && p.lessonid && p.sessionid){
        this.getData(p.courseid, p.lessonid, p.sessionid);
        this.user = await this.auth.getUser() as Student;

        this.activeDrawing$.subscribe(async(v) => {
          if(!v){
            return;
          }

          this.activeDrawing = v;
        });
      }else{
        this.router.navigate(['/courses']);
      }
    })
  }

  getData(courseid: string, lessonid: string, sessionid: string){
    this.data.getLesson(courseid, lessonid).subscribe((res) => {
      if(res && res.data){
        console.log(res.data);
        this.lesson = res.data;

        const currentSession: Session = _.find(this.lesson.sessions, {_id: sessionid});

        if(!currentSession){
          this.router.navigate(['/courses']);
        }

        this.currentSession = currentSession;
        this.isTeacher = this.user && this.lesson.teacher_id === this.user._id;

        this.breadcrumbs.length = 2;
        this.breadcrumbs.push({
          link: this.lesson.course.topic,
          url: ['/courses', this.lesson.course_id, 'lesson', this.lesson._id]
        })
        this.breadcrumbs.push({
          link: `Session (${this.datePipe.transform(currentSession.startTime, 'shortDate')})`
        })

        this.getMaterials();
        this.getDrawings();
      }
    })
  }

  getMaterials(){
    this.data.getMaterials({session_id: this.currentSession._id}).subscribe((res) => {
      if(res && res.data){
        this.materials = res.data;
      }
    })
  }

  uploadMaterials(){
    try{
      swal.mixin({
        title: 'Select file',
        input: 'file',
        inputAttributes: {
          'accept': 'application/msword, application/vnd.ms-powerpoint, application/pdf, image/*',
          'aria-label': 'Upload your file'
        },
        confirmButtonText: 'Next &rarr;',
        showCancelButton: true,
        progressSteps: ['1', '2'],
      }).queue([
        {
          title: 'Select file',
        },
        {
          title: 'Enter file description',
          input: 'text',
          confirmButtonText: 'Submit',
          inputValidator: (v) => !!v ? '' : 'Please enter valid description',
        }
      ]).then(async({value}) => {
        if (value && value.length === 2) {
          try{
            this.isLoadingMaterials = true;

            const fileData: Material = {};

            const file: File = value[0];

            fileData.description = value[1];
            fileData.course_id = this.lesson.course_id;
            fileData.lesson_id = this.lesson._id;
            fileData.session_id = this.currentSession._id;

            if(file.size > 10 * 1000 * 1000){
              throw Error('File is too big');
            }

            switch(file.type){
              case 'application/pdf': {
                fileData.type = 'pdf';
                break;
              }
              case 'application/msword': {
                fileData.type = 'doc';
                break;
              }
              case 'application/vnd.ms-powerpoint': {
                fileData.type = 'ppt';
                break;
              }
              default: {
                if(/^image\//.test(file.type)){
                  fileData.type = 'img';
                  break;
                }

                throw Error('File type is forbidden.');
              }
            }

            let key = uuid();

            key = `${key}.${_.last(_.split(file.name, '.'))}`;

            const res = await this.data.presignS3File(key, file.type).toPromise();

            if(!res || !res.url){
              throw Error('Something went wrong.');
            }

            await this.data.uploadS3File(res.url, file).toPromise();

            fileData.url = `https://bilinstudio-assets.s3.us-east-2.amazonaws.com/${key}`;

            await this.data.addMaterial(fileData).toPromise();

            this.getMaterials();

          }catch({message}){
            this.toastr.error(message);
          }finally{
            this.isLoadingMaterials = false;
          }
        }
      })

    }catch({message}){
      this.toastr.error(message);
    }
  }

  /**
   * Function that gets all drawings from DB
   */
  getDrawings(){
    this.data.getDrawings({session_id: this.currentSession._id}).subscribe((res) => {
      if(res && res.data){
        this.drawings = res.data;
        this.activeDrawing$.next(res.data[0]);
      }
    })
  }

  uploadDrawings(){
    try{
      swal.fire({
        title: 'Select drawing',
        input: 'file',
        inputAttributes: {
          'accept': 'image/*',
          'aria-label': 'Upload your file'
        },
        confirmButtonText: 'Next &rarr;',
        showCancelButton: true,
        allowOutsideClick: () => !swal.isLoading(),
        showLoaderOnConfirm: true,
        preConfirm: (value) => new Promise(async (resolve) => {
          try{
            if(!value){
              throw Error('Please select an image.');
            }

            const fileData: Drawing = {};

            const file: File = value;

            fileData.course_id = this.lesson.course_id;
            fileData.lesson_id = this.lesson._id;
            fileData.session_id = this.currentSession._id;

            if(file.size > 10 * 1000 * 1000){
              throw Error('File is too big');
            }

            let key = uuid();

            key = `${key}.${_.last(_.split(file.name, '.'))}`;

            const res = await this.data.presignS3File(key, file.type).toPromise();

            if(!res || !res.url){
              throw Error('Something went wrong.');
            }

            await this.data.uploadS3File(res.url, file).toPromise();

            fileData.url = `https://bilinstudio-assets.s3.us-east-2.amazonaws.com/${key}`;
            fileData.uploaded_by = this.user._id;

            resolve(fileData);
          }catch({message}){
            swal.showValidationMessage(message);
            return resolve();
          }
        })
      }).then(async({value}) => {
        try{
          this.isLoadingDrawings = true;

          if (value) {
            this.data.addDrawing(value).subscribe((res) => {
              if(res){
                this.getDrawings();
              }
            })
          }
        }catch({message}){
          this.toastr.error(message);
        }finally{
          this.isLoadingDrawings = false;
        }
      })

    }catch({message}){
      this.toastr.error(message);
    }
  }

  async reportDrawing(){
    try{
      console.log(this.activeDrawing._id);

      this.toastr.success('The drawing has been successfully reported.');

    }catch({message}){
      this.toastr.error(message);
    }
  }

  async deleteDrawing(){
    await this.data.deleteDrawing(this.activeDrawing._id).toPromise();
    this.getDrawings();
  }

  async deleteMaterial(id: string){
    await this.data.deleteMaterial(id).toPromise();
    this.getMaterials();
  }
}
